import type { TFunction } from 'i18next';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@shared/auth/use-auth';

import { DEFAULT_MODEL_ID, isSessionModelUnknown, type ModelName } from '../data/models';
import type { ThinkingChoice } from '../data/thinking-levels';
import { conversationHasInvestorData } from '../data-policy/system-notes';
import { isBlankConversation, useConversationsStore } from '../store/conversations.store';
import { useThinkingLevelStore } from '../store/thinking-level.store';
import type {
  AttachedFile,
  CompactionOutcome,
  Conversation,
  DeleteOutcome,
  InteractionAnswer,
  MessageFeedback,
  MeterFigure,
  ProposalDecision,
} from '../types';

export interface UseChat {
  conversation: Conversation | null;
  /** Id of the model selected for the active conversation (the picker resolves its label from /models). */
  modelId: string;
  /** The active chat's model's label and provider as picked: names it once /models omits it. */
  modelName?: ModelName;
  /**
   * True when the active conversation is bound to a session it did not start (a
   * history stub, or a chat persisted without `sessionStartedHere`): that session
   * may run a model picked elsewhere, so with no choice the picker names no model.
   */
  sessionModelUnknown: boolean;
  /**
   * True when the active conversation is a connected session: it runs the deployment's model,
   * so the control hides its model panes.
   */
  connected: boolean;
  /**
   * The active chat's thinking choice; with no active chat, or one kept before levels were per
   * chat, the user's last choice (the one a first chat is seeded with).
   */
  thinking: ThinkingChoice | null;
  /** Sets the active chat's thinking choice (a new chat first, with none). */
  setThinking: (choice: ThinkingChoice) => void;
  /** The active chat's Agentic switch; off with no active chat. */
  agentic: boolean;
  /** Sets the active chat's Agentic switch (a new chat first, with none). */
  setAgentic: (on: boolean) => void;
  /** True when the active chat is a fresh, never-used chat (show the empty state). */
  isEmpty: boolean;
  /** Restoring a server-known conversation's transcript: 'loading' / 'error', else null. */
  historyState: 'loading' | 'error' | null;
  /** First name for the greeting, falling back gracefully. */
  greetingName: string;
  /** Time-of-day greeting prefix, e.g. "Good afternoon" (per the AI CIO spec). */
  greeting: string;
  waitingForPolicy: boolean;
  chatHasInvestorData: boolean;
  send: (text: string, files?: AttachedFile[]) => void;
  newChat: () => void;
  startNewChatWith: (modelId: string) => string;
  selectConversation: (id: string) => void;
  /** Sets the active chat's model, with the picked model's name (a new chat first, with none). */
  setModelId: (id: string, name?: ModelName) => void;
  setFeedback: (messageId: string, feedback: MessageFeedback) => void;
  regenerate: (messageId: string) => void;
  answerWithInvestbanqModel: (messageId: string) => void;
  answerInteraction: (answer: InteractionAnswer) => void;
  decideProposal: (decision: ProposalDecision) => void;
  renameConversation: (id: string, title: string) => void;
  deleteConversation: (id: string) => Promise<DeleteOutcome>;
  /** A reply is being written in the active chat: a send is queued, and Stop replaces Send. */
  busy: boolean;
  /** Stop was pressed and the reply has not ended. */
  stopping: boolean;
  /** The reply being written is one this tab does not read: one line says "Still answering". */
  following: boolean;
  /** A compaction holds the active chat's session: the composer waits, with no Stop. */
  compacting: boolean;
  /**
   * The token meter's figure for the active chat: null where no meter shows (no session yet, no
   * figure, a model with no window, a session a press found gone).
   */
  meter: MeterFigure | null;
  /**
   * The cut the divider marks: the last turn the session's stored compaction folded; null for
   * none, and for a session a press found gone.
   */
  compactedThroughTurn: number | null;
  /** How this tab's last press in the active chat ended, until it is taken off. */
  compactionNote: CompactionOutcome | null;
  /**
   * The control is held: a press from the chat's model answered that compaction on Investbanq's
   * own model is not available yet. Another model may compact, so a model change frees it.
   */
  compactOff: boolean;
  /** The control is left out: compaction is not offered on the session. */
  compactHidden: boolean;
  /** Compacts the active chat. */
  compact: () => void;
  /** Takes the outcome of the active chat's last press off the composer. */
  dismissCompactionNote: () => void;
  /** Stops the reply being written; its stream goes on to the stop card. */
  stop: () => void;
  removeQueued: (messageId: string) => void;
  /** Takes the queued messages back to edit: their texts, a paragraph each. */
  takeBackQueued: () => Promise<string>;
  /** "Send now" for queued messages that wait for the user: too old, or held by a stop. */
  sendQueuedNow: () => void;
}

/** Time-of-day greeting buckets per the AI CIO spec. */
function greetingForHour(hour: number, t: TFunction<'ai-cio'>): string {
  if (hour >= 5 && hour <= 11) return t('greeting.morning', 'Good morning');
  if (hour >= 12 && hour <= 17) return t('greeting.afternoon', 'Good afternoon');
  if (hour >= 18 && hour <= 22) return t('greeting.evening', 'Good evening');
  return t('greeting.night', 'Good night');
}

/**
 * Binds the shared conversations store to a component. Both the full AI CIO
 * page and the floating panel use this so they operate on the same state.
 */
export function useChat(): UseChat {
  const { t } = useTranslation('ai-cio');
  const { user } = useAuth();
  const activeId = useConversationsStore((s) => s.activeId);
  const conversations = useConversationsStore((s) => s.conversations);
  const sendMessage = useConversationsStore((s) => s.sendMessage);
  const newChatAction = useConversationsStore((s) => s.newChat);
  const selectConversation = useConversationsStore((s) => s.selectConversation);
  const setModel = useConversationsStore((s) => s.setModel);
  const setThinking = useConversationsStore((s) => s.setThinking);
  const setAgentic = useConversationsStore((s) => s.setAgentic);
  const lastChoice = useThinkingLevelStore((s) => s.choice);
  const setFeedbackAction = useConversationsStore((s) => s.setFeedback);
  const regenerateAction = useConversationsStore((s) => s.regenerate);
  const answerInteractionAction = useConversationsStore((s) => s.answerInteraction);
  const decideProposalAction = useConversationsStore((s) => s.decideProposal);
  const renameConversation = useConversationsStore((s) => s.renameConversation);
  const deleteConversation = useConversationsStore((s) => s.deleteConversation);
  const stopTurn = useConversationsStore((s) => s.stopTurn);
  const removeQueuedAction = useConversationsStore((s) => s.removeQueued);
  const takeBackQueuedAction = useConversationsStore((s) => s.takeBackQueued);
  const sendQueuedNowAction = useConversationsStore((s) => s.sendQueuedNow);
  const syncSession = useConversationsStore((s) => s.syncSession);
  const compactAction = useConversationsStore((s) => s.compact);
  const dismissCompactionNoteAction = useConversationsStore((s) => s.dismissCompactionNote);
  const activity = useConversationsStore((s) => (activeId ? s.activity[activeId] : undefined));

  const conversation = activeId ? (conversations[activeId] ?? null) : null;
  // A server-known stub (sessionId, no messages yet) is real history, not an empty chat.
  const isEmpty = !conversation || isBlankConversation(conversation);
  const historyState = useConversationsStore((s) =>
    activeId ? (s.historyStatus[activeId] ?? null) : null,
  );
  const loadTranscript = useConversationsStore((s) => s.loadTranscript);
  const needsTranscript =
    conversation !== null &&
    conversation.sessionId !== undefined &&
    conversation.messages.length === 0;

  // DEV-4243: reopening a conversation from the server index restores its transcript.
  useEffect(() => {
    if (needsTranscript && activeId) void loadTranscript(activeId);
  }, [needsTranscript, activeId, loadTranscript]);

  // A reply may still be written in the chat's session (from before a reload, or in
  // another tab), with messages queued behind it or a stop card open.
  const sessionId = conversation?.sessionId;
  useEffect(() => {
    if (activeId && sessionId) void syncSession(activeId);
  }, [activeId, sessionId, syncSession]);

  const send = useCallback(
    (text: string, files?: AttachedFile[]) => {
      void sendMessage(text, files);
    },
    [sendMessage],
  );

  const newChat = useCallback(() => {
    newChatAction();
  }, [newChatAction]);

  const startNewChatWith = useCallback(
    (modelId: string) => newChatAction(modelId, { forceNew: true }),
    [newChatAction],
  );

  const regenerate = useCallback(
    (messageId: string) => {
      void regenerateAction(messageId);
    },
    [regenerateAction],
  );

  const answerWithInvestbanqModel = useCallback(
    (messageId: string) => {
      void regenerateAction(messageId, { forceInternal: true });
    },
    [regenerateAction],
  );

  const answerInteraction = useCallback(
    (answer: InteractionAnswer) => {
      void answerInteractionAction(answer);
    },
    [answerInteractionAction],
  );

  const decideProposal = useCallback(
    (decision: ProposalDecision) => {
      void decideProposalAction(decision);
    },
    [decideProposalAction],
  );

  const stop = useCallback(() => {
    if (activeId) void stopTurn(activeId);
  }, [activeId, stopTurn]);

  const removeQueued = useCallback(
    (messageId: string) => {
      if (activeId) void removeQueuedAction(activeId, messageId);
    },
    [activeId, removeQueuedAction],
  );

  const takeBackQueued = useCallback(
    (): Promise<string> => (activeId ? takeBackQueuedAction(activeId) : Promise.resolve('')),
    [activeId, takeBackQueuedAction],
  );

  const sendQueuedNow = useCallback(() => {
    if (activeId) sendQueuedNowAction(activeId);
  }, [activeId, sendQueuedNowAction]);

  const compact = useCallback(() => {
    if (activeId) void compactAction(activeId);
  }, [activeId, compactAction]);

  const dismissCompactionNote = useCallback(() => {
    if (activeId) dismissCompactionNoteAction(activeId);
  }, [activeId, dismissCompactionNoteAction]);

  // The meter shows only for a chat with a session, a figure and the model's window.
  const figure = activity && !activity.sessionGone ? activity.context : null;
  const meter =
    sessionId !== undefined && figure && figure.windowTokens !== null
      ? { ...figure, windowTokens: figure.windowTokens }
      : null;

  return {
    conversation,
    modelId: conversation?.modelId ?? DEFAULT_MODEL_ID,
    modelName: conversation?.modelName,
    sessionModelUnknown: conversation !== null && isSessionModelUnknown(conversation),
    connected: conversation?.connected === true,
    thinking:
      conversation && conversation.thinking !== undefined ? conversation.thinking : lastChoice,
    setThinking,
    agentic: conversation?.agentic === true,
    setAgentic,
    isEmpty,
    historyState,
    waitingForPolicy:
      conversation?.messages.some((m) => m.status === 'streaming' && m.waiting !== undefined) ??
      false,
    chatHasInvestorData: conversationHasInvestorData(conversation),
    greetingName: user?.firstName ?? t('greeting.fallbackName', 'there'),
    greeting: greetingForHour(new Date().getHours(), t),
    send,
    newChat,
    startNewChatWith,
    selectConversation,
    setModelId: setModel,
    setFeedback: setFeedbackAction,
    regenerate,
    answerWithInvestbanqModel,
    answerInteraction,
    decideProposal,
    renameConversation,
    deleteConversation,
    busy: activity?.busy ?? false,
    stopping: activity?.stopping ?? false,
    following: activity?.following ?? false,
    compacting: activity?.compacting ?? false,
    meter,
    compactedThroughTurn: activity && !activity.sessionGone ? activity.compactedThroughTurn : null,
    compactionNote: activity?.compactionNote ?? null,
    compactOff: conversation !== null && activity?.compactOffFor === conversation.modelId,
    compactHidden: activity?.compactHidden ?? false,
    compact,
    dismissCompactionNote,
    stop,
    removeQueued,
    takeBackQueued,
    sendQueuedNow,
  };
}
